/* eslint-disable import/order */
import { FC, useState } from "react";

import { useNavigate } from "react-router-dom";
import { Modal, Form, Input, Checkbox } from "antd";
import { useTranslation } from "react-i18next";
import { Button } from "common/lib/components";
import { URLs } from "common/lib/constants";
import { authApiReducer } from "domains/authentication/store/auth.api.reducer";
import { liquidityApiReducer } from "domains/liquidity/store/liquidity.api.reducer";
import { SecurityDisclaimer } from "domains/trustben/components";

import { useValidationSchemas } from "common/shared/constants/validationSchemas";
import { useValidationErrorMessages } from "common/shared/constants/validationErrorMessages";
import { phoneRegEx } from "common/shared/constants/validationRegExps";

interface IFormData {
  name: string;
  email: string;
  phone: string;
  aqNumber: string;
  sendCopy: boolean;
}

interface IProps {
  onRequestFormalProposal?: (value: string) => Promise<void> | void;
  LRId: string;
  onSuccess?: () => void; // optional callback for parent to close modal / handle UI
  showFeedback?: boolean; // allow parent to suppress internal success modal
  displayId?: string;
  altQuoteSubmissionEmail?: string;
}

const RequestFormalProposalButton: FC<IProps> = ({ 
  onRequestFormalProposal, 
  LRId, 
  onSuccess, 
  showFeedback = true,
  displayId,
  altQuoteSubmissionEmail,
}) => {
  const { t } = useTranslation([ "altQuotes", "dashboard" ]);
  const navigate = useNavigate();
  const [ logout ] = authApiReducer.useLogoutMutation();
  const [ updateLiquidityRequest, { isLoading } ] = liquidityApiReducer.useIsFormalProposalRequestedMutation();
    const { phoneUSValidationSchema } = useValidationSchemas();
  const { required } = useValidationErrorMessages();
    const [ isModalVisible, setIsModalVisible ] = useState(false);
  const [ form ] = Form.useForm<IFormData>();

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  // Strict US phone validation: allow optional country code; enforce 10 digits with valid area/exchange.
  const validateUSPhone = async (_: unknown, value?: string) => {
    if (!value) return Promise.resolve();

    const digits = value.replace(/\D/g, "");
    const core = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

    if (core.length === 10 && /^[2-9]\d{2}[2-9]\d{6}$/.test(core)) {
      return Promise.resolve();
    }

    return Promise.reject(new Error(t("validation.phoneUS", { ns: "common" })));
  };

  const handleRequest = async (formData: IFormData) => {
    if (!LRId) return;

    try {
      // if (onRequestFormalProposal) {
      //   await onRequestFormalProposal(LRId);
      // }

      await updateLiquidityRequest({
        LRId,
        changes: { 
          is_formal_proposal_requested: true,
          alt_quote_submission_name: formData.name,
          alt_quote_submission_email: formData.email,
          alt_quote_submission_phone_number: formData.phone,
        },
      }).unwrap?.();

      // notify parent via callback
      onSuccess?.();

      // close modal
      setIsModalVisible(false);
      form.resetFields();

      // show confirmation to user unless parent suppressed it
      if (showFeedback) {
        Modal.success({
          className: "ben-modal-success", // custom class for styling
          title: t("Request Sent"),
          content: (
            <>
              <div style={ { marginBottom: 16 } }>
                Thank you for requesting a formal proposal. A member of the Beneficient team will get back to you by email shortly with any additional information requests that are needed to provide the proposal. Meanwhile, if you wish to reach out, please contact us at{" "}
                <a href="mailto:investops@beneficient.com">investops@beneficient.com</a>.
              </div>
              <SecurityDisclaimer />
            </>
          ),
          okText: t("OK", { ns: "common" }),
          okButtonProps: { className: "ben-modal-success-ok", type: "primary" },
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Request formal proposal error:", error);
      Modal.error({
        title: t("Error"),
        content: t("Failed to request formal proposal. Please try again."),
      });
    }
  };

  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();
      await handleRequest(values);
    } catch (error) {
      // Form validation error - let antd handle it
    }
  };

  return (
    <>
      <div className="ben-apply-for-liquidity-button-container">
        <Button type="primary" loading={ isLoading } disabled={ isLoading } onClick={ showModal }>
          {t("Request Formal Proposal")}
        </Button>
      </div>

      <Modal
        title={
          <div style={ { 
            textAlign: 'center', 
            fontFamily: 'IBM Plex Sans SemiBold',
            color: '#148ba9',
            fontWeight: '600',
          } }
          >
            AltQuote Proposal Request
          </div>
        }
        open={ isModalVisible }
        onCancel={ handleModalCancel }
        footer={ [
          <Button 
            key="cancel" 
            onClick={ handleModalCancel }
          >
            Cancel
          </Button>,
          <Button 
            key="submit" 
            type="primary"
            loading={ isLoading }
            onClick={ handleModalSubmit }
          >
            Submit
          </Button>,
        ] }
        width={ 600 }
      >
        <div style={ { marginBottom: 16 } }>
          Please complete your contact information and submit. A Beneficient team
          member will get back to you shortly with additional information requests
          needed to provide the proposal.
        </div>

        <Form
          form={ form }
          layout="vertical"
          initialValues={ {
            aqNumber: displayId || "AQ",
            email: altQuoteSubmissionEmail || "",
            sendCopy: false,
          } }
        >
          <Form.Item
            label="Name"
            name="name"
            rules={ [ { required: true, message: 'Please enter your name' } ] }
          >
            <Input placeholder="Enter your full name" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={ [
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' },
            ] }
          >
            <Input placeholder="Enter your email address" />
          </Form.Item>

          <Form.Item
            label="Phone"
            name="phone"
            rules={ [ 
              { required: true, message: required }, 
              { validator: validateUSPhone },
            ] }
          >
            <Input placeholder="Enter your phone number" />
          </Form.Item>

          <Form.Item
            label="AQ Number"
            name="aqNumber"
              rules={ [ { required: true, message: 'Please enter the AQ number' } ] }
          >
            <Input placeholder="AQ" disabled />
          </Form.Item>

          <div style={ { margin: '16px 0', borderTop: '1px solid #f0f0f0' } } />

          <SecurityDisclaimer />
        </Form>
      </Modal>
    </>
  );
};

export default RequestFormalProposalButton;
